import { Fragment, useState } from 'react';
import { Trash2, ChevronDown, Plus, Check, X } from 'lucide-react';
import { useConfirm } from '@/shared/modals/ConfirmDialog';
import { useMembers, useAddMember, useUpdateMember, useRemoveMember, ASSIGNABLE_PERMISSIONS, type Member } from './settings.api';

const AV_COLORS = ['#84CC16', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4'];

function PermissionChecks({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }): JSX.Element {
  const full = value.includes('*');
  const toggle = (key: string) => onChange(value.includes(key) ? value.filter((p) => p !== key) : [...value.filter((p) => p !== '*'), key]);
  return (
    <div className="col gap8">
      <label className="row aic gap8 t-sm" style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={full} style={{ accentColor: '#0E7490', width: 15, height: 15 }} onChange={(e) => onChange(e.target.checked ? ['*'] : [])} />
        Acceso total
      </label>
      {!full && (
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {ASSIGNABLE_PERMISSIONS.map((p) => (
            <label key={p.key} className="row aic gap8 t-sm muted" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={value.includes(p.key)} style={{ accentColor: '#0E7490', width: 15, height: 15 }} onChange={() => toggle(p.key)} />
              {p.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function permSummary(perms: string[]): string {
  if (perms.includes('*')) return 'Acceso total';
  if (perms.length === 0) return 'Sin permisos';
  const labels = perms.map((k) => ASSIGNABLE_PERMISSIONS.find((p) => p.key === k)?.label ?? k);
  return labels.slice(0, 2).join(', ') + (labels.length > 2 ? ` +${labels.length - 2}` : '');
}

export function UsersTab(): JSX.Element {
  const { data: members, isLoading } = useMembers();
  const add = useAddMember();
  const update = useUpdateMember();
  const remove = useRemoveMember();
  const confirm = useConfirm();

  const [nw, setNw] = useState({ email: '', name: '', password: '', permissions: [] as string[] });
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    add.mutate(
      { email: nw.email.trim(), name: nw.name.trim(), password: nw.password, role: 'member', permissions: nw.permissions },
      {
        onSuccess: () => {
          setNw({ email: '', name: '', password: '', permissions: [] });
          setAdding(false);
        },
      },
    );
  };

  return (
    <div className="cx">
      <div className="card" style={{ maxWidth: 760 }}>
        <div className="card-head">
          <div className="card-title">Usuarios y permisos</div>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding((v) => !v)}>
            {adding ? <X size={15} /> : <Plus size={15} />}
            {adding ? 'Cancelar' : 'Invitar usuario'}
          </button>
        </div>

        {adding && (
          <form onSubmit={submitNew} className="card-pad" style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)' }}>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="field">
                <label className="label">Email</label>
                <input className="input" type="email" required value={nw.email} onChange={(e) => setNw((s) => ({ ...s, email: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">Nombre</label>
                <input className="input" required value={nw.name} onChange={(e) => setNw((s) => ({ ...s, name: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">Contraseña</label>
                <input className="input" type="password" minLength={8} required value={nw.password} onChange={(e) => setNw((s) => ({ ...s, password: e.target.value }))} />
              </div>
            </div>
            <div className="field" style={{ marginBottom: 14 }}>
              <label className="label">Permisos</label>
              <PermissionChecks value={nw.permissions} onChange={(p) => setNw((s) => ({ ...s, permissions: p }))} />
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={add.isPending}>
              <Check size={15} />
              {add.isPending ? 'Agregando…' : 'Agregar usuario'}
            </button>
          </form>
        )}

        {isLoading ? (
          <div className="card-pad muted t-sm">Cargando…</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Rol</th>
                  <th>Permisos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(members ?? []).map((m: Member) => {
                  const c = AV_COLORS[(m.name?.length ?? 0) % AV_COLORS.length]!;
                  const isOwner = m.role === 'owner';
                  const open = openId === m.userId;
                  return (
                    <Fragment key={m.userId}>
                      <tr>
                        <td>
                          <div className="row aic gap10">
                            <span className="av" style={{ width: 32, height: 32, fontSize: 12, background: `${c}22`, color: c, borderColor: `${c}33` }}>
                              {(m.name || '?').charAt(0).toUpperCase()}
                            </span>
                            <div>
                              <div className="strong">{m.name}</div>
                              <div className="t-xs muted">{m.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${isOwner ? 'yellow' : 'gray'}`}>{m.role}</span>
                        </td>
                        <td className="t-sm muted">{permSummary(m.permissions)}</td>
                        <td className="right">
                          {isOwner ? (
                            <span className="t-xs muted">Owner</span>
                          ) : (
                            <div className="row gap4" style={{ justifyContent: 'flex-end' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(open ? null : m.userId)}>
                                Permisos
                                <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                              </button>
                              <button
                                className="btn btn-ghost btn-sm btn-icon"
                                style={{ color: 'var(--error)' }}
                                onClick={async () => {
                                  if (await confirm({ title: '¿Remover usuario?', description: `${m.email} perderá acceso a esta empresa.`, confirmLabel: 'Remover', variant: 'danger' })) {
                                    remove.mutate(m.userId);
                                  }
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={4} style={{ background: 'var(--canvas)' }}>
                            <PermissionChecks value={m.permissions} onChange={(p) => update.mutate({ userId: m.userId, permissions: p })} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
